import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Form, Button } from 'reactstrap';
import FormInput from '@/components/forms/FormInput';
import {
  AdminAgentDealUpdateRequestDto,
  CreateAgentDealUpdateRequestDto,
  UpdateAgentDealUpdateRequestDto,
} from '@repo/common-types';
import FormAsyncSelect from '@/components/forms/FormAsyncSelect';
import { fetchUsersRest } from '@/services/users/users-rest';
import { UserInfo } from '@/services/users/user-types';
import { fetchActiveAgentDealByUserIdRest } from '@/services/agents/agents-service';

type UserSelectOption = {
  value: string; // userId
  label: string; // "alias - email"
  user: UserInfo;
};

interface Props {
  onClose: () => void;
  onSubmit: (dto: CreateAgentDealUpdateRequestDto | UpdateAgentDealUpdateRequestDto) => void;
  isLoading?: boolean;
  agentDealUpdateRequest?: AdminAgentDealUpdateRequestDto;
}

const validationSchema = Yup.object({
  user: Yup.mixed<UserSelectOption>().required('User must be selected'),
  agentDealId: Yup.string().uuid('Invalid agent deal').required('Agent deal is required'),
  currentPercentage: Yup.number().min(0).max(100).required('Current % is required'),
  newPercentage: Yup.number().min(5, 'Min 5%').max(45, 'Max 45%').required('New % is required'),
}).required();

// Form data type that matches CreateAgentDealDto but with string dates for form inputs
type AgentDealFormData = Yup.InferType<typeof validationSchema>;

const AgentDealUpdateRequestFormModal: React.FC<Props> = ({ onClose, onSubmit, isLoading, agentDealUpdateRequest }) => {
  const { control, handleSubmit, watch, setValue, setError, clearErrors } = useForm<AgentDealFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      user: agentDealUpdateRequest?.user
        ? {
            value: agentDealUpdateRequest.user.id,
            label: `${agentDealUpdateRequest.user.alias} - ${agentDealUpdateRequest.user.email}`,
            user: agentDealUpdateRequest.user as unknown as UserInfo,
          }
        : undefined,
      agentDealId: agentDealUpdateRequest?.agentDealId,
      currentPercentage: agentDealUpdateRequest?.currentPercentage,
      newPercentage: agentDealUpdateRequest?.newPercentage,
    },
    mode: 'onChange',
  });

  const user = watch('user');

  useEffect(() => {
    const fetchAgentDeal = async () => {
      if (!user || !user.value) {
        // Clear the fields if no user is selected
        setValue('agentDealId', '');
        setValue('currentPercentage', 0);
        clearErrors('agentDealId');
        return;
      }

      try {
        const agentDeal = await fetchActiveAgentDealByUserIdRest(user.value);

        if (agentDeal) {
          setValue('agentDealId', agentDeal.id);
          setValue('currentPercentage', agentDeal.rakeSharePercentage);
          clearErrors('agentDealId');
        } else {
          // No active agent deal found
          setValue('agentDealId', '');
          setValue('currentPercentage', 0);
          setError('agentDealId', {
            type: 'manual',
            message: 'This user does not have an active agent deal',
          });
        }
      } catch (error) {
        console.error('Error fetching agent deal:', error);
        setValue('agentDealId', '');
        setValue('currentPercentage', 0);
        setError('agentDealId', {
          type: 'manual',
          message: 'Failed to fetch agent deal',
        });
      }
    };

    // Only fetch if we're creating a new update (not editing existing)
    if (!agentDealUpdateRequest) {
      fetchAgentDeal();
    }
  }, [user, agentDealUpdateRequest, setValue, setError, clearErrors]);

  const handleFormSubmit = (formData: AgentDealFormData) => {
    if (agentDealUpdateRequest) {
      // Edit mode - only update new percentage
      const dto: UpdateAgentDealUpdateRequestDto = {
        newPercentage: formData.newPercentage,
      };
      onSubmit(dto);
    } else {
      // Create mode
      const { user, ...rest } = formData;
      const dto: CreateAgentDealUpdateRequestDto = {
        ...rest,
        userId: user.value,
      };
      onSubmit(dto);
    }
  };

  // Async user options loader
  const loadUserOptions = async (inputValue: string) => {
    if (inputValue.length < 2) return [];
    try {
      const result = await fetchUsersRest({ skip: 0, take: 25, filter: { alias: inputValue } });
      return result.users.map((user: UserInfo) => ({ value: user.id, label: `${user.alias} - ${user.email}`, user }));
    } catch (e) {
      console.error('Error loading users:', e);
      return [];
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md" centered unmountOnClose>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalHeader toggle={onClose} className="bg-dark px-4 py-3">
          {agentDealUpdateRequest ? 'Edit Agent Deal Update Request' : 'Create Agent Deal Update Request'}
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={12}>
              {agentDealUpdateRequest ? (
                <div className="form-group">
                  <label className="form-label">User</label>
                  <input
                    type="text"
                    className="form-control"
                    value={`${agentDealUpdateRequest.user?.alias || ''} - ${agentDealUpdateRequest.user?.email || ''}`}
                    disabled
                    readOnly
                  />
                </div>
              ) : (
                <Controller
                  name="user"
                  control={control}
                  render={({ field }) => (
                    <FormAsyncSelect
                      {...field}
                      loadOptions={loadUserOptions}
                      placeholder="Search for a user by alias..."
                      label="User"
                    />
                  )}
                />
              )}
            </Col>
            <Col md={12}>
              <FormInput name="agentDealId" label="Agent Deal ID" control={control} placeholder="-" disabled />
            </Col>
            <Col md={6}>
              <FormInput name="currentPercentage" label="Current %" control={control} type="number" min={0} disabled />
            </Col>
            <Col md={6}>
              <FormInput name="newPercentage" label="New %" control={control} type="number" min={5} />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end px-4 py-3">
          <div className="d-flex gap-2">
            <Button type="button" outline color="dark" onClick={onClose} disabled={!!isLoading}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={!!isLoading}>
              {isLoading ? 'Saving...' : agentDealUpdateRequest ? 'Update' : 'Create Update'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AgentDealUpdateRequestFormModal;
